import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import { MdClose, MdChevronLeft, MdChevronRight } from "react-icons/md";

Modal.setAppElement("body");

// Server URLs carry no MIME type, so sniff the extension - same approach the
// activity feed uses to render note attachments (Activities/NoteItem.js).
const getAttachmentKind = (url) => {
  const value = url.toLowerCase().split("?")[0];
  if (value.includes(".jpg") || value.includes(".jpeg") || value.includes(".png")) return "image";
  if (value.includes(".pdf")) return "pdf";
  return "other";
};

const Attachment = ({ url }) => {
  const kind = getAttachmentKind(url);
  const fileName = url.split("?")[0].split("/").pop();

  if (kind === "image") {
    return (
      <img
        src={url}
        alt="Proof of Return"
        style={{ maxWidth: "80vw", maxHeight: "70vh", objectFit: "contain" }}
      />
    );
  }
  if (kind === "pdf") {
    return (
      <iframe
        src={url}
        title={fileName}
        width="100%"
        height="600px"
        style={{ border: "none", minWidth: "60vw" }}
        data-testid="proof-of-return-pdf"
      />
    );
  }
  return (
    <div className="text-muted">Preview not available for this file type.</div>
  );
};

/**
 * Shows the ticket's Proof of Return attachment(s) (`dor_attachment`,
 * `dor_attachment_2..5`) full size, one at a time. Images render inline,
 * PDFs in an iframe; anything else falls back to a message - opening the URL
 * directly is the only way to download those. Prev/next controls appear only
 * when there's more than one attachment.
 */
export function ProofOfReturnModal({ isOpen, onClose, urls, ticketNumber }) {
  const attachments = urls || [];
  const [index, setIndex] = useState(0);

  // Land back on the first attachment each time the modal is reopened (or
  // shown for a different ticket) rather than remembering the last position.
  useEffect(() => {
    if (isOpen) setIndex(0);
  }, [isOpen, urls]);

  if (attachments.length === 0) return null;

  const currentIndex = Math.min(index, attachments.length - 1);
  const hasMultiple = attachments.length > 1;
  const goPrev = () =>
    setIndex((i) => (i - 1 + attachments.length) % attachments.length);
  const goNext = () => setIndex((i) => (i + 1) % attachments.length);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Proof of Return"
      // .confirm-modal alone is locked to 380px; -ticket makes it width: auto.
      className="confirm-modal confirm-modal-ticket"
      style={{ overlay: { zIndex: 1100 } }}
    >
      <div className="card" data-testid="proof-of-return-modal">
        <button onClick={onClose} className="confirm-modal-close">
          <MdClose />
        </button>
        <div className="card-header">
          {ticketNumber ? `Proof of Return - ${ticketNumber}` : "Proof of Return"}
        </div>
        <div className="card-body" style={{ maxHeight: "80vh", overflowY: "auto" }}>
          {hasMultiple && (
            <div className="d-flex align-items-center justify-content-between mb-2">
              <button
                type="button"
                className="k-btn k-btn-outline-primary k-btn-sm"
                onClick={goPrev}
                aria-label="Previous attachment"
              >
                <MdChevronLeft />
              </button>
              <div className="text-muted">
                Attachment {currentIndex + 1} of {attachments.length}
              </div>
              <button
                type="button"
                className="k-btn k-btn-outline-primary k-btn-sm"
                onClick={goNext}
                aria-label="Next attachment"
              >
                <MdChevronRight />
              </button>
            </div>
          )}
          <Attachment url={attachments[currentIndex]} />
        </div>
      </div>
    </Modal>
  );
}

export default ProofOfReturnModal;
